import type { FC } from 'react';
import React, { useCallback } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel.ts';

import {
	ContentIcon,
	Subtitle,
	LastAction,
	StatusLozenge,
	Title,
	SocialProof,
	Excerpt,
	PageLink,
	PageCardContainer,
	AutomationDiscoveryMessage,
	AudioPlaylistButton,
} from '../Items/index.ts';

const styles = cssMap({
	container: {
		width: '400px',
	},
	header: {
		width: '100%',
		minWidth: '0px',
	},
	headerIcon: {
		display: 'flex',
		justifyContent: 'space-between',
		flexShrink: 0,
	},
	titleContainer: {
		minWidth: '0px',
		maxWidth: '100%',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContainer = styled.div({
	flex: '1 0 auto',
	display: 'flex',
	alignItems: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Footer = styled.div({
	width: '100%',
	marginTop: token('space.200'),
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

type CardFooterProps = {
	isEndOfPage?: boolean;
};

const CardFooter: FC<CardFooterProps> = ({ isEndOfPage }) => {
	return (
		<FooterContainer>
			<Footer>
				<LastAction />
				<Inline alignBlock="center" space="space.100">
					<SocialProof />
					<AudioPlaylistButton isEndOfPage={isEndOfPage} />
				</Inline>
			</Footer>
		</FooterContainer>
	);
};

type PopupViewProps = {
	shouldUseAutomationDiscovery?: boolean; // this should probably go into PageTreeContext, however since this is the only usage, it's ok to leave it here for now
	isEndOfPage?: boolean;
};

export const PopupView: FC<PopupViewProps> = ({ shouldUseAutomationDiscovery, isEndOfPage }) => {
	return (
		<PopupViewBase isEndOfPage={isEndOfPage}>
			<PageLink
				shouldHideFolder
				featureAttributionCode={isEndOfPage ? FeatureCode.EOP_REC_CARD : FeatureCode.PAGE_TREE_CARD}
			/>
			<Box padding="space.200" xcss={styles.container} testId="page-card-popup-view">
				<Stack alignBlock="start" alignInline="start" space="space.200">
					<Inline
						alignBlock="center"
						alignInline="start"
						shouldWrap={false}
						grow="fill"
						space="space.100"
					>
						<Box xcss={styles.headerIcon} as="span">
							<ContentIcon />
						</Box>
						<Stack xcss={styles.header} alignBlock="start" alignInline="start" grow="fill">
							<Box xcss={styles.titleContainer}>
								<Title singleLine />
							</Box>
							<Subtitle />
						</Stack>
						<StatusLozenge />
					</Inline>
				</Stack>
				<Excerpt />
				<CardFooter isEndOfPage={isEndOfPage} />
			</Box>
			<AutomationDiscoveryMessage shouldUseAutomationDiscovery={shouldUseAutomationDiscovery} />
		</PopupViewBase>
	);
};

const PopupViewBase = ({
	children,
	isEndOfPage,
}: {
	children: React.ReactNode;
	isEndOfPage?: boolean;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handlePopupClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluence',
				action: 'clicked',
				actionSubject: isEndOfPage ? 'relatedContentCard' : 'spacePageCard',
				actionSubjectId: 'excerptPopup',
				attributes: {
					source: isEndOfPage ? 'endOfPageRecommendations' : 'pageTree',
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isEndOfPage]);

	return (
		<PageCardContainer isHoverPageCard onClick={handlePopupClick}>
			{children}
		</PageCardContainer>
	);
};
